import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import Select, { components } from 'react-select';
import IcomoonReact from 'icomoon-react';
import { axiosAuthed } from '../../../../shared/utils/setCommonHeaders';
import iconSet from '../../../../shared/images/teambuildr-selection.json';

const ChooserContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 260px;
  font-family: 'Nunito Sans', sans-serif;
  color: #444444;
`;

const SelectWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const GradientBorder = styled.div`
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  background: linear-gradient(90deg, #267deb 0%, #5d45f9 100%);
  border-radius: 6px;
  z-index: 0;
  pointer-events: none;
`;

const StatusMessage = styled.div`
  position: absolute;
  top: calc(100% + 10px);
  left: 50%;
  transform: translateX(-50%);
  font-size: 12px;
  font-weight: 600;
  color: ${(props) => (props.variant === 'error' ? '#D32F2F' : '#444444')};
  background: ${(props) => (props.variant === 'error' ? '#FFEBEE' : '#ffffff')};
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid
    ${(props) => (props.variant === 'error' ? '#FFCDD2' : '#eeeeee')};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  white-space: nowrap;
  z-index: 10;

  /* Arrow pointing up */
  &::before,
  &::after {
    content: '';
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-style: solid;
  }

  /* Outer arrow (border) */
  &::before {
    top: -7px;
    border-width: 0 7px 7px 7px;
    border-color: transparent transparent
      ${(props) => (props.variant === 'error' ? '#FFCDD2' : '#eeeeee')}
      transparent;
  }

  /* Inner arrow (fill) */
  &::after {
    top: -6px;
    border-width: 0 6px 6px 6px;
    border-color: transparent transparent
      ${(props) => (props.variant === 'error' ? '#FFEBEE' : '#ffffff')}
      transparent;
  }
`;

const PlaceholderWithIcon = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.5;
  height: 100%;
  justify-content: flex-start;
  color: #444444;
`;

const GroupHeader = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #9e9e9e;
  padding: 8px 16px;
  background-color: #f9f9f9;
  border-bottom: 1px solid #eeeeee;
`;

const customSelectStyles = {
  control: (provided) => ({
    ...provided,
    minHeight: '40px',
    height: '40px',
    border: 'none',
    borderRadius: '4px',
    boxShadow:
      '0px 1px 3px rgba(48, 49, 51, 0.1), 0px 0px 1px rgba(48, 49, 51, 0.05)',
    backgroundColor: '#ffffff',
    position: 'relative',
    zIndex: 1,
    '&:hover': {
      borderColor: 'transparent',
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '0 16px',
    height: '38px',
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 999,
    border: '1px solid #EEEEEE',
    boxShadow:
      '0px 16px 24px rgba(48, 49, 51, 0.1), 0px 0px 1px rgba(48, 49, 51, 0.05)',
    borderRadius: '4px',
    overflow: 'hidden',
  }),
  menuList: (provided) => ({
    ...provided,
    paddingTop: 0,
    paddingBottom: 0,
    maxHeight: '320px',
    overflowY: 'auto',
    overflowX: 'hidden',
  }),
  option: (provided, state) => {
    let backgroundColor = '#FFFFFF';
    if (state.isSelected) {
      backgroundColor = '#FCFCFC';
    } else if (state.isFocused) {
      backgroundColor = '#f5f5f5';
    }

    return {
      ...provided,
      backgroundColor,
      color: '#444444',
      cursor: 'pointer',
      padding: '8px 16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    };
  },
  placeholder: (provided) => ({
    ...provided,
    margin: 0,
    color: '#9E9E9E',
    display: 'flex',
    alignItems: 'center',
    height: '100%',
  }),
  clearIndicator: () => ({
    display: 'none',
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: '#9E9E9E',
    '&:hover': {
      color: '#9E9E9E',
    },
  }),
  input: (provided) => ({
    ...provided,
    color: '#444444',
    margin: 0,
    padding: 0,
    '& input': {
      font: 'inherit',
    },
    '& input::placeholder': {
      color: '#9E9E9E',
      opacity: 1,
    },
  }),
  group: (provided) => ({
    ...provided,
    paddingTop: 0,
    paddingBottom: 0,
  }),
  groupHeading: (provided) => ({
    ...provided,
    margin: 0,
    padding: 0,
  }),
};

// Custom components for react-select
const CustomSingleValue = ({ children, ...props }) => (
  <components.SingleValue {...props}>
    <PlaceholderWithIcon>
      <IcomoonReact
        iconSet={iconSet}
        icon='calendar2'
        size={20}
        color='#9E9E9E'
      />
      {children}
    </PlaceholderWithIcon>
  </components.SingleValue>
);

CustomSingleValue.propTypes = {
  children: PropTypes.node,
};
CustomSingleValue.defaultProps = {
  children: null,
};

const CustomOption = (props) => {
  const {
    children, isSelected, isFocused, innerRef, innerProps,
  } = props;

  return (
    <div
      ref={innerRef}
      {...innerProps}
      style={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '8px 16px',
        backgroundColor: isSelected ? '#FCFCFC' : isFocused ? '#f5f5f5' : '#FFFFFF',
        color: isSelected ? '#BDBDBD' : '#444444',
        cursor: 'pointer',
        fontFamily: 'Nunito Sans',
        fontWeight: 400,
        fontSize: '16px',
        lineHeight: '150%',
        width: '100%',
        boxSizing: 'border-box',
      }}
      onMouseEnter={(e) => {
        if (!isSelected) {
          e.currentTarget.style.backgroundColor = '#f5f5f5';
          e.currentTarget.style.color = '#444444';
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = isSelected
          ? '#FCFCFC'
          : isFocused
            ? '#f5f5f5'
            : '#FFFFFF';
        e.currentTarget.style.color = isSelected ? '#BDBDBD' : '#444444';
      }}
    >
      <span
        style={{
          flex: '1',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {children}
      </span>
      {isSelected && (
        <IcomoonReact
          iconSet={iconSet}
          icon='checkmark'
          size={16}
          color='#9E9E9E'
          style={{
            flexShrink: 0,
            marginLeft: '8px',
          }}
        />
      )}
    </div>
  );
};

CustomOption.propTypes = {
  children: PropTypes.node,
  isSelected: PropTypes.bool,
  isFocused: PropTypes.bool,
  innerRef: PropTypes.func,
  innerProps: PropTypes.shape({}),
};
CustomOption.defaultProps = {
  children: null,
  isSelected: false,
  isFocused: false,
  innerRef: undefined,
  innerProps: undefined,
};

const CustomGroupHeading = ({ children, innerProps }) => (
  <GroupHeader {...innerProps}>{children}</GroupHeader>
);

CustomGroupHeading.propTypes = {
  children: PropTypes.node,
  innerProps: PropTypes.shape({}),
};
CustomGroupHeading.defaultProps = {
  children: null,
  innerProps: {},
};

const CustomPlaceholder = ({
  isDataReady,
  menuIsOpen,
  placeholderText,
  ...props
}) => {
  if (!isDataReady) return null;
  if (menuIsOpen) return null;

  return (
    <components.Placeholder {...props}>
      <PlaceholderWithIcon>
        <IcomoonReact
          iconSet={iconSet}
          icon='calendar2'
          size={20}
          color='#9E9E9E'
        />
        <span style={{ fontWeight: 700 }}>{placeholderText}</span>
      </PlaceholderWithIcon>
    </components.Placeholder>
  );
};

CustomPlaceholder.propTypes = {
  isDataReady: PropTypes.bool.isRequired,
  menuIsOpen: PropTypes.bool.isRequired,
  placeholderText: PropTypes.string.isRequired,
};

const CustomValueContainer = ({
  children,
  selectProps,
  menuIsOpen,
  isDataReady,
  ...restProps
}) => {
  const showOverlay = menuIsOpen && !selectProps.inputValue && isDataReady;

  return (
    <components.ValueContainer {...restProps} selectProps={selectProps}>
      {showOverlay && (
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            paddingLeft: '16px',
            color: '#9E9E9E',
            pointerEvents: 'none',
            zIndex: 1,
          }}
        >
          <IcomoonReact
            iconSet={iconSet}
            icon='calendar2'
            size={20}
            color='#9E9E9E'
          />
          <span style={{ marginLeft: '8px', fontWeight: 400 }}>
            Search for a calendar
          </span>
        </div>
      )}
      {children}
    </components.ValueContainer>
  );
};

CustomValueContainer.propTypes = {
  children: PropTypes.node,
  selectProps: PropTypes.shape({
    inputValue: PropTypes.string,
  }).isRequired,
  menuIsOpen: PropTypes.bool.isRequired,
  isDataReady: PropTypes.bool.isRequired,
};

CustomValueContainer.defaultProps = {
  children: null,
};

const CustomSingleValueWithMenu = ({ menuIsOpen, ...props }) => {
  if (menuIsOpen) return null;
  return <CustomSingleValue {...props} />;
};

CustomSingleValueWithMenu.propTypes = {
  menuIsOpen: PropTypes.bool.isRequired,
};

/**
 * CalendarChooser Component
 *
 * Searchable dropdown for selecting a calendar to plan.
 * Fetches calendars with plans from /accounts/{accountCode}/plans/calendars
 * and divides them into Calendar Groups and Athlete Calendars.
 */
const CalendarChooser = ({
  selectedCalendarId,
  onSelectCalendar,
  onCalendarNameFound,
  onCalendarsLoaded,
  accountCode,
  isDisabled,
}) => {
  const [calendars, setCalendars] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const selectRef = useRef(null);
  const hasReportedNameRef = useRef(false);

  // Keep a stable ref to the callback to avoid re-fetching when parent re-renders
  const onCalendarsLoadedRef = useRef(onCalendarsLoaded);
  useEffect(() => {
    onCalendarsLoadedRef.current = onCalendarsLoaded;
  }, [onCalendarsLoaded]);

  // Fetch calendars with plans
  useEffect(() => {
    // Track if this effect is still current (not stale from a previous accountCode)
    let isCurrent = true;

    if (accountCode) {
      // Reset calendars immediately when accountCode changes to prevent duplicates
      setCalendars([]);
      setIsLoading(true);
      setError(null);

      axiosAuthed
        .get(`/accounts/${accountCode}/plans/calendars`)
        .then((response) => {
          // Only update state if this effect is still current
          if (!isCurrent) return;

          const data = response.data || {};
          const accountCalendars = data.accountCalendars || [];
          const athleteCalendars = data.athleteCalendars || [];

          // Combine both into one array with a type indicator
          const allCalendars = [
            ...accountCalendars.map((cal) => ({
              ...cal,
              isAthleteCalendar: false,
            })),
            ...athleteCalendars.map((athlete) => ({
              ...athlete,
              isAthleteCalendar: true,
            })),
          ];

          setCalendars(allCalendars);
          setIsLoading(false);

          // Report calendars loaded status to parent
          if (onCalendarsLoadedRef.current) {
            onCalendarsLoadedRef.current(allCalendars.length === 0);
          }
        })
        .catch((err) => {
          // Only update state if this effect is still current
          if (!isCurrent) return;

          setError(err.message || 'Failed to load calendars');
          setIsLoading(false);

          // Report as no calendars on error
          if (onCalendarsLoadedRef.current) {
            onCalendarsLoadedRef.current(true);
          }
        });
    } else {
      // Clear calendars when no accountCode
      setCalendars([]);
    }

    // Cleanup: mark this effect as stale when accountCode changes
    return () => {
      isCurrent = false;
    };
  }, [accountCode]);

  const handleChange = (selectedOption) => {
    if (!onSelectCalendar) return;

    if (!selectedOption) {
      // Notify parent when selection is cleared so it can reset its state
      onSelectCalendar(null, null, null);
      return;
    }

    // Pass back either the calendarId or userId based on type, plus the label (name)
    if (selectedOption.type === 'athlete') {
      onSelectCalendar(selectedOption.userId, 'athlete', selectedOption.label);
    } else {
      onSelectCalendar(
        selectedOption.calendarId,
        'calendar',
        selectedOption.label,
      );
    }
  };

  // Group calendars into Calendar Groups (parent calendars) and Athlete Calendars (users)
  const getGroupedOptions = () => {
    if (!calendars || calendars.length === 0) return [];

    const calendarGroups = [];
    const athleteCalendars = [];

    calendars.forEach((item) => {
      if (item.isAthleteCalendar) {
        // This is an athlete/user calendar
        const athleteName = item.name || `User ${item.id}`;

        athleteCalendars.push({
          value: `athlete-${item.id}`,
          label: athleteName,
          type: 'athlete',
          userId: item.id,
          planId: item.planId || null,
        });
      } else {
        // This is a calendar group
        calendarGroups.push({
          value: String(item.id),
          label: item.name || `Calendar ${item.id}`,
          type: 'calendar',
          calendarId: item.id,
          planId: item.planId || null,
        });
      }
    });

    const groups = [];
    if (calendarGroups.length > 0) {
      groups.push({
        label: 'Calendar Groups',
        options: calendarGroups,
      });
    }
    if (athleteCalendars.length > 0) {
      groups.push({
        label: 'Athlete Calendars',
        options: athleteCalendars,
      });
    }

    return groups;
  };

  const groupedOptions = getGroupedOptions();

  // Find selected value by matching either calendarId or athlete-{userId}
  const selectedValue = groupedOptions
    .flatMap((group) => group.options)
    .find((opt) => {
      if (opt.type === 'calendar') {
        return (
          opt.calendarId === selectedCalendarId
          || opt.value === String(selectedCalendarId)
        );
      }
      if (opt.type === 'athlete') {
        return (
          opt.userId === selectedCalendarId
          || opt.value === `athlete-${selectedCalendarId}`
        );
      }
      return false;
    });

  // Report the calendar name when found (for restoring from localStorage)
  useEffect(() => {
    if (
      selectedValue?.label
      && onCalendarNameFound
      && !hasReportedNameRef.current
    ) {
      hasReportedNameRef.current = true;
      onCalendarNameFound(selectedValue.label);
    }
  }, [selectedValue, onCalendarNameFound]);

  // Reset the reported flag when calendar ID changes
  useEffect(() => {
    hasReportedNameRef.current = false;
  }, [selectedCalendarId]);

  const isDataReady = !isLoading && Array.isArray(calendars);

  let placeholderText = 'Select a calendar';
  if (isLoading) {
    placeholderText = 'Loading calendars...';
  } else if (error) {
    placeholderText = 'Unable to load calendars';
  } else if (calendars.length === 0) {
    placeholderText = 'No calendars available';
  }

  // Memoize custom components to prevent remounts on each render
  const selectComponents = useMemo(
    () => ({
      Placeholder: (props) => (
        <CustomPlaceholder
          {...props}
          isDataReady={isDataReady}
          menuIsOpen={menuIsOpen}
          placeholderText={placeholderText}
        />
      ),
      ValueContainer: (props) => (
        <CustomValueContainer
          {...props}
          menuIsOpen={menuIsOpen}
          isDataReady={isDataReady}
        />
      ),
      Option: CustomOption,
      SingleValue: (props) => (
        <CustomSingleValueWithMenu {...props} menuIsOpen={menuIsOpen} />
      ),
      GroupHeading: CustomGroupHeading,
    }),
    [isDataReady, menuIsOpen, placeholderText],
  );

  return (
    <ChooserContainer>
      <SelectWrapper>
        <GradientBorder />
        <Select
          ref={selectRef}
          inputId='planner-calendar-chooser'
          aria-label='Select calendar'
          styles={customSelectStyles}
          value={selectedValue || null}
          onChange={handleChange}
          options={groupedOptions}
          isLoading={isLoading}
          isDisabled={isDisabled}
          placeholder={placeholderText}
          hideSelectedOptions={false}
          isSearchable
          onMenuOpen={() => setMenuIsOpen(true)}
          onMenuClose={() => setMenuIsOpen(false)}
          components={selectComponents}
          classNamePrefix='react-select'
          filterOption={(option, searchText) => {
            if (!searchText) return true;
            return option.label
              .toLowerCase()
              .includes(searchText.toLowerCase());
          }}
          noOptionsMessage={() => 'No calendars found'}
        />
      </SelectWrapper>
      {error && !isDisabled && (
        <StatusMessage variant='error'>
          We could not load calendars. Please try again soon.
        </StatusMessage>
      )}
      {!isLoading && calendars.length === 0 && !error && !isDisabled && (
        <StatusMessage>
          Nothing scheduled yet. Create a calendar to get started.
        </StatusMessage>
      )}
    </ChooserContainer>
  );
};

CalendarChooser.propTypes = {
  selectedCalendarId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSelectCalendar: PropTypes.func.isRequired,
  onCalendarNameFound: PropTypes.func,
  onCalendarsLoaded: PropTypes.func,
  accountCode: PropTypes.string,
  isDisabled: PropTypes.bool,
};

CalendarChooser.defaultProps = {
  selectedCalendarId: null,
  onCalendarNameFound: null,
  onCalendarsLoaded: null,
  accountCode: null,
  isDisabled: false,
};

export default CalendarChooser;
